import { useEffect, useRef, useState } from "react";
import {
  apiPcf,
  type RoteiroLinha,
  type EtapaDisponivel,
  type LoteInfo,
  type EmpenhoLinha,
} from "../lib/api";
import { useTheme } from "../hooks/useTheme";
import { Logo } from "../components/Logo";
import { PesquisaLotesModal } from "../components/PesquisaLotesModal";
import { logout, type Usuario } from "../lib/auth";
import { MsgModal } from "../components/MsgModal";
import { confirmar } from "../lib/confirmar";
import { mascaraTempoHM } from "../lib/tempoHM";
import { mascaraDecimal } from "../lib/decimal";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";

// atcadpcf / pcf_etapas "Roteiro de Etapas": inclui/altera/exclui etapas da PCF, reordena
// (subir/descer, NOVA-POSICAO — também por arrastar) e grava a matéria-prima (lote) das etapas
// que movimentam estoque, com pesquisa de lote (CALLLOT) e de empenho (CALLEMPLOT). Fiel ao legado.

interface Props {
  usuario: Usuario;
  numpcf: number;
  onVoltar: () => void;
}

const nf = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nf3 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmt2 = (n: number) => nf.format(n || 0);
// PIC S9(n)V9(4) no legado: até 4 casas (QA 15/10/2026)
const limparDec = (v: string) => mascaraDecimal(v, 4);
const parseDec = (s: string) => Number(String(s).replace(",", ".")) || 0;

// MONTAR-TABELA L1421-1428: soma horas e minutos de HH,MM e transborda os minutos em horas.
function totalTempo(linhas: RoteiroLinha[]): string {
  let min = 0;
  for (const l of linhas) {
    const t = Number(l.tempoEtapa) || 0;
    const h = Math.floor(t);
    const m = Math.round((t - h) * 100);
    min += h * 60 + m;
  }
  return `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}min`;
}

export function PcfEtapas({ usuario, numpcf, onVoltar }: Props) {
  const { theme, toggle } = useTheme();
  const [linhas, setLinhas] = useState<RoteiroLinha[]>([]);
  const [etapas, setEtapas] = useState<EtapaDisponivel[]>([]);
  const [etapaCod, setEtapaCod] = useState(0);
  const [dimensionais, setDimensionais] = useState("");
  const [caracteristicas, setCaracteristicas] = useState("");
  const [tempo, setTempo] = useState("");
  const [numlot, setNumlot] = useState("");
  const [lote, setLote] = useState<LoteInfo | null>(null);
  const [qtd, setQtd] = useState("");
  const [editPos, setEditPos] = useState<number | null>(null); // posição em edição (null = novo)
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [pesqLote, setPesqLote] = useState(false);
  const [empenhos, setEmpenhos] = useState<EmpenhoLinha[] | null>(null);
  // Só a lista de empenhos ordena; o roteiro (arrastar) mantém a ordem da posição.
  const ordEmp = useOrdenacao(empenhos, {
    lot: (x) => x.numlot, loc: (x) => x.nomloc, pro: (x) => x.nompro, un: (x) => x.unidade, comp: (x) => x.comprimento, qtd: (x) => x.qtdemp,
  });
  const dragIdx = useRef<number | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const qtdRef = useRef<HTMLInputElement>(null);
  const numlotRef = useRef<HTMLInputElement>(null);

  function enterTab(e: React.KeyboardEvent) {
    if (e.key !== "Enter") return;
    const alvo = e.target as HTMLElement;
    if (alvo.tagName === "TEXTAREA") return;
    e.preventDefault();
    const foc = Array.from(
      formRef.current?.querySelectorAll<HTMLElement>("input,select,button") ?? []
    ).filter((x) => !(x as HTMLInputElement).disabled && x.tabIndex !== -1 && x.offsetParent !== null);
    const i = foc.indexOf(alvo);
    if (i >= 0 && i < foc.length - 1) foc[i + 1]?.focus();
  }

  const etapaSel = etapas.find((e) => e.codigo === etapaCod);
  const movimenta = !!etapaSel?.movimenta;

  async function recarregar() {
    setLinhas(await apiPcf.roteiro(numpcf));
  }

  useEffect(() => {
    void (async () => {
      try {
        const [r, e] = await Promise.all([apiPcf.roteiro(numpcf), apiPcf.etapas()]);
        setLinhas(r);
        setEtapas(e);
      } catch (ex) {
        setErro((ex as Error).message);
      }
    })();
  }, [numpcf]);

  // botao-limpar-lote (L494-503)
  function limparLote() {
    setNumlot("");
    setLote(null);
    setQtd("");
    numlotRef.current?.focus();
  }

  function limpar() {
    setEditPos(null);
    setEtapaCod(0);
    setDimensionais("");
    setCaracteristicas("");
    setTempo("");
    setNumlot("");
    setLote(null);
    setQtd("");
    setErro(null);
  }

  // MOSTRAR-LOTE (L603-656)
  async function carregarLote(nl: number) {
    if (nl <= 0) {
      setLote(null);
      return;
    }
    try {
      const info = await apiPcf.lote(nl);
      if (info.erro) {
        setErro(info.erro);
        setLote(null);
        numlotRef.current?.focus();
        return;
      }
      setErro(null);
      setNumlot(String(nl));
      setLote(info);
      setQtd("");
      setTimeout(() => qtdRef.current?.focus(), 0);
    } catch (ex) {
      setErro((ex as Error).message);
    }
  }

  // CALLEMPLOT + MOSTRAR-EMPENHO (L515-589)
  async function abrirEmpenhos() {
    try {
      setEmpenhos(await apiPcf.empenhos(numpcf));
    } catch (ex) {
      setErro((ex as Error).message);
    }
  }
  async function escolherEmpenho(e: EmpenhoLinha) {
    setEmpenhos(null);
    try {
      const info = await apiPcf.empenho(numpcf, e.numlot, e.seqemp);
      if (info.erro) {
        setErro(info.erro);
        numlotRef.current?.focus();
        return;
      }
      setErro(null);
      setNumlot(String(e.numlot));
      setLote(info);
      setQtd(String(info.qtdEmpenho ?? 0).replace(".", ","));
      setTimeout(() => qtdRef.current?.focus(), 0);
    } catch (ex) {
      setErro((ex as Error).message);
    }
  }

  // T4-QTD_SELECIONADA-WS LostFocus (L668-681)
  function conferirQtd() {
    if (!lote) return;
    const q = parseDec(qtd);
    if (lote.ehKg && q > lote.saldoPeso) setErro("Peso Selecionado superior ao saldo em estoque!");
    else if (!lote.ehKg && q > lote.saldoPecas) setErro("Quantidade Selecionado superior ao saldo em estoque!");
  }

  async function gravar() {
    setErro(null);
    setOk(null);
    const payload = {
      numpcf,
      etapaCodigo: etapaCod,
      dimensionais,
      caracteristicas,
      tempoEtapa: parseDec(tempo),
      numlot: movimenta && lote ? Number(numlot) || 0 : 0,
      qtdSelecionada: movimenta && lote ? parseDec(qtd) : 0,
    };
    try {
      const r =
        editPos == null
          ? await apiPcf.roteiroIncluir(payload)
          : await apiPcf.roteiroAlterar({ ...payload, posicao: editPos });
      if (!r.ok) {
        setErro(r.mensagem);
        return;
      }
      setOk(editPos == null ? "Etapa incluída." : "Etapa alterada.");
      limpar();
      await recarregar();
    } catch (ex) {
      setErro((ex as Error).message);
    }
  }

  // TABELA-DblClick (L915-989): carrega a etapa; a operação fica travada no modo alteração.
  async function editar(l: RoteiroLinha) {
    setEditPos(l.posicao);
    setEtapaCod(l.etapa);
    setDimensionais(l.dimensionais);
    setCaracteristicas(l.caracteristicas);
    setTempo(String(l.tempoEtapa).replace(".", ","));
    setErro(null);
    setOk(null);
    try {
      const lt = await apiPcf.roteiroLote(numpcf, l.posicao, l.etapa);
      if (lt && lt.numlot) {
        setNumlot(String(lt.numlot));
        setQtd(String(lt.qtd).replace(".", ","));
        const info = await apiPcf.lote(lt.numlot, false);
        setLote(info.erro ? null : info);
      } else {
        setNumlot("");
        setQtd("");
        setLote(null);
      }
    } catch {
      /* lote opcional */
    }
  }

  async function excluir(l: RoteiroLinha) {
    if (!(await confirmar(`Excluir a etapa ${l.posicao} — ${l.descricao}?`))) return;
    try {
      await apiPcf.roteiroExcluir(numpcf, l.posicao, l.etapa);
      if (editPos === l.posicao) limpar();
      await recarregar();
    } catch (ex) {
      setErro((ex as Error).message);
    }
  }

  // Arrastar = NOVA-POSICAO: a etapa arrastada vai para a posição da linha onde foi solta.
  async function soltar(destino: number) {
    const origem = dragIdx.current;
    dragIdx.current = null;
    if (origem == null || origem === destino) return;
    const de = linhas[origem];
    const para = linhas[destino];
    if (!de || !para) return;
    try {
      await apiPcf.roteiroNovaPosicao(numpcf, de.posicao, para.posicao);
      await recarregar();
    } catch (ex) {
      setErro((ex as Error).message);
      await recarregar();
    }
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <Logo />
          <h1>
            Plano de Controle de Fabricação - Etapas da Produção
            <small>PCF nº {numpcf}</small>
          </h1>
        </div>
        <span className="spacer" />
        <div className="user-badge" title={`Usuário: ${usuario.nomusu}`}>
          <span className="user-avatar">{usuario.apelid.charAt(0).toUpperCase()}</span>
          <span className="user-nome">{usuario.nomusu.split(" ")[0]}</span>
        </div>
        <button className="btn-secundario" onClick={onVoltar}>
          ← PCF
        </button>
        <button className="btn-secundario" onClick={toggle} title={theme === "light" ? "Modo escuro" : "Modo claro"}>{theme === "light" ? "Modo escuro" : "Modo claro"}</button>
        <button className="btn-secundario" onClick={logout} title="Sair">Sair</button>
      </header>

      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <MsgModal texto={ok} tipo="ok" onClose={() => setOk(null)} />

      <div className="pcf-form" ref={formRef}>
        <section className="pcf-sec">
          <h2>{editPos == null ? "Incluir Etapa" : `Alterar — Posição ${editPos}`}</h2>
          <div className="pcf-grid">
            <label className="pcf-campo">
              <span>Etapa</span>
              <select
                value={etapaCod}
                disabled={editPos != null}
                onKeyDown={enterTab}
                onChange={(e) => {
                  setEtapaCod(Number(e.target.value));
                  setNumlot("");
                  setLote(null);
                  setQtd("");
                }}
              >
                <option value={0}>— selecione —</option>
                {etapas.map((e) => (
                  <option key={e.codigo} value={e.codigo}>
                    {e.descri}
                    {e.movimenta ? " ⬚" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="pcf-campo pcf-lg">
              <span>Dimensões</span>
              <input value={dimensionais} maxLength={50} onKeyDown={enterTab} onChange={(e) => setDimensionais(e.target.value)} />
            </label>
            <label className="pcf-campo pcf-lg">
              <span>Características</span>
              <input value={caracteristicas} maxLength={50} onKeyDown={enterTab} onChange={(e) => setCaracteristicas(e.target.value)} />
            </label>
            <label className="pcf-campo pcf-sm">
              <span>Duração (hhhh,mm)</span>
              <input className="num" inputMode="decimal" value={tempo} onKeyDown={enterTab} placeholder="h,mm" title="Horas,minutos (ex.: 1,30 = 1h30)" onChange={(e) => setTempo(mascaraTempoHM(e.target.value, tempo))} />
            </label>
          </div>

          {movimenta && (
            <div className="pcf-grid pcf-materia">
              <h3 style={{ flex: "1 1 100%", margin: 0 }}>Matéria Prima a ser Utilizada</h3>
              <label className="pcf-campo pcf-sm">
                <span>Lote</span>
                <input
                  ref={numlotRef}
                  className="num"
                  inputMode="numeric"
                  value={numlot}
                  disabled={editPos != null}
                  onChange={(e) => setNumlot(e.target.value.replace(/\D/g, ""))}
                  onBlur={() => {
                    if (editPos == null && (Number(numlot) || 0) > 0 && Number(numlot) !== lote?.numlot) void carregarLote(Number(numlot));
                  }}
                  onKeyDown={enterTab}
                />
              </label>
              {editPos == null && (
                <>
                  <button className="btn-secundario" onClick={() => setPesqLote(true)}>
                    Pesquisar Lotes
                  </button>
                  <button className="btn-secundario" onClick={() => void abrirEmpenhos()}>
                    Pesquisar Empenhos
                  </button>
                  <button className="btn-secundario" onClick={limparLote}>
                    Limpar Lote
                  </button>
                </>
              )}
              {lote && (
                <>
                  <label className="pcf-campo pcf-lg">
                    <span>Matéria Prima</span>
                    <input value={lote.nommat} readOnly tabIndex={-1} />
                  </label>
                  <label className="pcf-campo">
                    <span>Local de Estoque</span>
                    <input value={lote.nomloc} readOnly tabIndex={-1} />
                  </label>
                  <label className="pcf-campo pcf-sm">
                    <span>Comprimento</span>
                    <input className="num" value={lote.comprimento} readOnly tabIndex={-1} />
                  </label>
                  <label className="pcf-campo pcf-sm">
                    <span>Unidade</span>
                    <input value={lote.unidade} readOnly tabIndex={-1} />
                  </label>
                  <label className="pcf-campo pcf-sm">
                    <span>{lote.ehKg ? "Saldo em KG" : "Saldo em Peças"}</span>
                    <input className="num" value={lote.ehKg ? nf3.format(lote.saldoPeso) : fmt2(lote.saldoPecas)} readOnly tabIndex={-1} />
                  </label>
                  <label className="pcf-campo pcf-sm">
                    <span>{lote.ehKg ? "Peso Selecionado" : "Qtd Selecionada"}</span>
                    <input
                      ref={qtdRef}
                      className="num"
                      inputMode="decimal"
                      value={qtd}
                      onKeyDown={enterTab}
                      onBlur={conferirQtd}
                      onChange={(e) => setQtd(lote.ehKg ? limparDec(e.target.value) : e.target.value.replace(/\D/g, ""))}
                    />
                  </label>
                </>
              )}
            </div>
          )}

          <div className="pcf-acoes">
            <button className="btn-acao" onClick={() => void gravar()}>
              {editPos == null ? "Incluir Etapa" : "Alterar"}
            </button>
            <button className="btn-secundario" onClick={limpar}>
              Limpar
            </button>
          </div>
        </section>

        <section className="pcf-sec">
          <h2>
            Etapas <small className="pcf-hint">(arraste para levar a etapa a outra posição)</small>
            <span className="spacer" />
            <small className="pcf-hint">Tempo Total (hh,mm): {totalTempo(linhas)}</small>
          </h2>
          <div className="tabela-wrap">
            <table className="tabela-pcfs">
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <th className="num">Posição</th>
                  <th>Etapa</th>
                  <th>Descrição</th>
                  <th>Características</th>
                  <th className="num">Duração</th>
                  <th style={{ width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {linhas.length === 0 && (
                  <tr>
                    <td colSpan={7} className="plan-vazio">
                      Nenhuma etapa no roteiro.
                    </td>
                  </tr>
                )}
                {linhas.map((l, i) => (
                  <tr
                    key={`${l.posicao}-${l.etapa}`}
                    className={"row-drag" + (editPos === l.posicao ? " plan-sel" : "")}
                    draggable
                    onDragStart={() => (dragIdx.current = i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => void soltar(i)}
                    onDoubleClick={() => void editar(l)}
                  >
                    <td className="drag-handle" title="Arraste">⠿</td>
                    <td className="num">{l.posicao}</td>
                    <td>{l.descricao}</td>
                    <td>{l.dimensionais}</td>
                    <td>{l.caracteristicas}</td>
                    <td className="num">{String(l.tempoEtapa).replace(".", ",")}</td>
                    <td className="pcf-row-acoes">
                      <button className="btn-mini" title="Alterar" onClick={() => void editar(l)}>Alterar</button>
                      <button className="btn-mini btn-mini-del" title="Eliminar" onClick={() => void excluir(l)}>Eliminar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="pcf-acoes">
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>
            Voltar
          </button>
        </div>
      </div>

      {pesqLote && (
        <PesquisaLotesModal
          onFechar={() => setPesqLote(false)}
          onEscolher={(nl) => {
            setPesqLote(false);
            void carregarLote(nl);
          }}
        />
      )}

      {empenhos && (
        <div className="pcf-modal-bg" onClick={() => setEmpenhos(null)}>
          <div className="pcf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pcf-modal-topo">
              <b>Pesquisa de Lotes Empenhados no Pedido</b>
              <button className="btn-secundario" onClick={() => setEmpenhos(null)}>Fechar</button>
            </div>
            <p className="pcf-hint">Para selecionar, clique no registro desejado.</p>
            <div className="tabela-wrap">
              <table className="tabela-pcfs">
                <thead>
                  {/* callemplot LEITURA (TELA-1.prc L137-143) — colunas do form (Pesquisas.ppj); a 7ª
                      (seqemp, rótulo técnico "sEQEMP") é a chave usada no clique e não aparece. */}
                  <tr>
                    <ThOrdena ord={ordEmp} col="lot" className="num">Lote</ThOrdena>
                    <ThOrdena ord={ordEmp} col="loc">Local de Estoque</ThOrdena>
                    <ThOrdena ord={ordEmp} col="pro">Produto</ThOrdena>
                    <ThOrdena ord={ordEmp} col="un">Unidade</ThOrdena>
                    <ThOrdena ord={ordEmp} col="comp" className="num">Comprimento</ThOrdena>
                    <ThOrdena ord={ordEmp} col="qtd" className="num">Selecionado</ThOrdena>
                  </tr>
                </thead>
                <tbody>
                  {empenhos.length === 0 && (
                    <tr>
                      <td colSpan={6} className="plan-vazio">
                        Nenhum empenho ativo para o item do pedido desta PCF.
                      </td>
                    </tr>
                  )}
                  {ordEmp.linhas.map((x) => (
                    <tr key={x.seqemp} className="row-click" onClick={() => void escolherEmpenho(x)}>
                      <td className="num">{x.numlot}</td>
                      <td>{x.nomloc}</td>
                      <td>{x.nompro}</td>
                      <td>{x.unidade}</td>
                      <td className="num">{x.comprimento}</td>
                      <td className="num">{x.unidade.toUpperCase() === "KG" ? nf3.format(x.qtdemp) : fmt2(x.qtdemp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
